const STORAGE_KEY = "practido_traffic_attribution";

/*
 * Parses URL query parameters and captures only present marketing/ad parameters into localStorage.
 * Safely preserves first-touch attribution across internal website navigation.
 */
export const captureTrafficSource = () => {
  try {
    if (typeof window === "undefined" || !window.location) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmContent = urlParams.get("utm_content");
    const utmTerm = urlParams.get("utm_term");
    const gclid = urlParams.get("gclid");
    const fbclid = urlParams.get("fbclid");

    // Check if any marketing/ad parameter is present in current URL
    const hasMarketingParams = Boolean(
      utmSource ||
      utmMedium ||
      utmCampaign ||
      utmContent ||
      utmTerm ||
      gclid ||
      fbclid,
    );

    // If no marketing parameters in the current URL, do not overwrite existing attribution
    if (!hasMarketingParams) {
      return;
    }

    // Build attribution object with ONLY the keys that exist
    const attribution = {};

    if (utmSource) attribution.utm_source = utmSource.trim();
    if (utmMedium) attribution.utm_medium = utmMedium.trim();
    if (utmCampaign) attribution.utm_campaign = utmCampaign.trim();
    if (utmContent) attribution.utm_content = utmContent.trim();
    if (utmTerm) attribution.utm_term = utmTerm.trim();
    if (gclid) attribution.gclid = gclid.trim();
    if (fbclid) attribution.fbclid = fbclid.trim();

    // Fallback: If gclid exists without utm_source, default utm_source to google_ads
    if (gclid && !attribution.utm_source) {
      attribution.utm_source = "google_ads";
    }

    // Fallback: If fbclid exists without utm_source, default utm_source to meta_ads
    if (fbclid && !attribution.utm_source) {
      attribution.utm_source = "meta_ads";
    }

    attribution.capturedAt = new Date().toISOString();

    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch (error) {
    console.error("Error capturing traffic source:", error);
  }
};

/**
 * Retrieves stored attribution data from localStorage.
 */
export const getStoredTrafficSource = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return null;
    }
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

/*
 * Dynamically formats the source string for demo booking and Admin Email.
 * Examples:
 * - Only utm_source -> "practido-website (google_ads)"
 * - utm_source + utm_medium -> "practido-website (google / cpc)"
 * - utm_source + utm_medium + utm_campaign -> "practido-website (google / cpc / summer_robotics)"
 * - None -> "practido-website"
 */
export const getFormattedBookingSource = () => {
  const attribution = getStoredTrafficSource();

  if (!attribution) {
    return "practido-website";
  }

  // Collect only present parts in order: source, medium, campaign
  const parts = [
    attribution.utm_source,
    attribution.utm_medium,
    attribution.utm_campaign,
  ].filter(Boolean);

  if (parts.length > 0) {
    return `practido-website (${parts.join(" / ")})`;
  }

  return "practido-website";
};
